/**
 * Builds staged `VehicleDraft.manualOverrideActions` from page-level edits to a feed-backed vehicle.
 *
 * This is the product path that replaces the crude direct `/vehicle-manual-overrides` calls: the
 * edit page collects all dirty display fields on Save and this pure function maps them to the
 * backend-staged action shape. It NEVER writes the committed VehicleManualOverride store (that
 * happens at publish) and is intentionally side-effect free so it can be unit tested directly.
 *
 * Manual override settings are PER FIELD: each edited feed-backed field becomes its own
 * `manualOverrideActions` entry (and eventually its own `VehicleManualOverride` row keyed by
 * jazelIdVin + field), so release policy and expiry are resolved independently per field. There is
 * no single page-level policy.
 *
 * Guardrails enforced here:
 * - Action names match the backend `ManualOverrideDraftActionType` exactly
 *   (`ADD_OVERRIDE` | `UPDATE_OVERRIDE` | `REMOVE_OVERRIDE`).
 * - Provenance is used when available for audit/source detail. Curated manual-fill fields can be
 *   staged without provenance; in that case source metadata is omitted and mappedValueAtLock is null.
 * - `mappedValueAtLock` comes from the existing ACTIVE override when updating one, otherwise the
 *   base mapped/feed value — never the overlaid display value.
 * - `UNTIL_DATE` requires `expiresAt` for that specific field; other policies on other fields are
 *   unaffected.
 */

import type { ActiveManualOverride, ManualOverrideReleasePolicy } from '@/services/inventory'

/** Product default release policy for a newly overridden field (per field, not page-level). */
export const DEFAULT_OVERRIDE_RELEASE_POLICY: ManualOverrideReleasePolicy = 'UNTIL_FEED_VALUE_CHANGES'

/** Fields whose product default release policy differs from the global default. */
const FIELD_DEFAULT_RELEASE_POLICIES: Record<string, ManualOverrideReleasePolicy> = {
  // Uploaded photos have no feed value to track; keep them until the user releases the override.
  dealer_photos: 'MANUAL_RELEASE_ONLY',
}

/**
 * Per-field product default release policy (used only when no explicit/active/pending policy
 * exists). `fallback` is the global default for fields without a specific one.
 */
export function getDefaultReleasePolicyForField(
  field: string,
  fallback: ManualOverrideReleasePolicy = DEFAULT_OVERRIDE_RELEASE_POLICY,
): ManualOverrideReleasePolicy {
  return FIELD_DEFAULT_RELEASE_POLICIES[field] ?? fallback
}

export type ResolvedFieldSource = {
  sourceId: string
  sourceFeed: string
  sourceField: string
  sourcePath: string
}

export type FieldProvenance = {
  resolvedSources?: ResolvedFieldSource[]
  provenanceMissing?: boolean
}

export type FieldProvenanceMap = Record<string, FieldProvenance>

/** Backend-staged action payload (server stamps updatedAt/changedBy). */
export type ManualOverrideActionPayload =
  | {
      action: 'ADD_OVERRIDE' | 'UPDATE_OVERRIDE'
      manualValue: unknown
      mappedValueAtLock: unknown
      releasePolicy: ManualOverrideReleasePolicy
      expiresAt?: string
      sourceFeed?: string
      sourceField?: string
      sourcePath?: string
    }
  | { action: 'REMOVE_OVERRIDE' }

/** A single field's user-selected override settings (explicit choices only). */
export type FieldOverrideSetting = {
  releasePolicy?: ManualOverrideReleasePolicy
  /** ISO datetime (already converted from any local input by the caller). */
  expiresAt?: string
}

/** Pending draft action shape relevant to building (carries its own policy/expiry). */
export type PendingActionInput = {
  action?: string
  mappedValueAtLock?: unknown
  releasePolicy?: ManualOverrideReleasePolicy
  expiresAt?: string
}

export type BuildManualOverrideActionsParams = {
  /** Changed display fields -> new page value (operational/photo fields already excluded). */
  dirtyFields: Record<string, unknown>
  /** Pre-edit baseline values (used for mappedValueAtLock when no ACTIVE override exists). */
  baseline: Record<string, unknown>
  /** Per-field resolved provenance from GET /mapping-config/fields. */
  provenance: FieldProvenanceMap
  /** Committed ACTIVE overrides, keyed by field (from vehicle-by-vin). */
  activeOverrides: Record<string, ActiveManualOverride>
  /** Existing pending draft actions, keyed by field (decides ADD vs UPDATE, stabilizes base). */
  pendingActions?: Record<string, PendingActionInput>
  /** Explicit per-field override settings (user selections). Absent fields fall back by precedence. */
  fieldSettings?: Record<string, FieldOverrideSetting>
  /** Product default policy for a field with no explicit/active/pending policy. */
  defaultReleasePolicy: ManualOverrideReleasePolicy
  /** Fields where the user removed an existing ACTIVE override (stages REMOVE_OVERRIDE). */
  removedOverrideFields?: string[]
}

export type BuildManualOverrideActionsResult = {
  actions: Record<string, ManualOverrideActionPayload>
  /** Retained for callers; curated manual-fill fields no longer block on missing provenance. */
  blockedFields: string[]
  /** Fields whose policy is UNTIL_DATE but have no valid expiresAt (per field). */
  missingExpiresAtFields: string[]
}

function normalizeMappedValueAtLock(value: unknown): unknown {
  return value === undefined ? null : value
}

function firstResolvedSource(provenance: FieldProvenance | undefined): ResolvedFieldSource | null {
  const source = provenance?.resolvedSources?.[0]
  if (!source) return null
  if (!source.sourceFeed || !source.sourceField || !source.sourcePath) return null
  return source
}

/**
 * Resolves the mapped base value to lock against. Preference order (guardrail 4):
 *   1. existing ACTIVE override's mappedValueAtLock (preserve the real base on update),
 *   2. an already-staged pending action's mappedValueAtLock (stable across repeated edits),
 *   3. the pre-edit baseline value (the base mapped/feed value when nothing is overridden yet).
 */
function resolveMappedValueAtLock(
  field: string,
  params: BuildManualOverrideActionsParams,
): unknown {
  const active = params.activeOverrides[field]
  if (active) return active.mappedValueAtLock
  const pending = params.pendingActions?.[field]
  if (pending && 'mappedValueAtLock' in pending) return pending.mappedValueAtLock
  return params.baseline[field]
}

/**
 * Resolves a field's effective release policy. Precedence: explicit user setting >
 * existing ACTIVE override > pending draft action > per-field product default.
 */
export function resolveFieldReleasePolicy(
  field: string,
  params: Pick<
    BuildManualOverrideActionsParams,
    'fieldSettings' | 'activeOverrides' | 'pendingActions' | 'defaultReleasePolicy'
  >,
): ManualOverrideReleasePolicy {
  return (
    params.fieldSettings?.[field]?.releasePolicy ??
    params.activeOverrides[field]?.releasePolicy ??
    params.pendingActions?.[field]?.releasePolicy ??
    getDefaultReleasePolicyForField(field, params.defaultReleasePolicy)
  )
}

/** Resolves a field's effective expiresAt (ISO) using the same precedence as the policy. */
function resolveFieldExpiresAt(
  field: string,
  params: BuildManualOverrideActionsParams,
): string | undefined {
  const candidate =
    params.fieldSettings?.[field]?.expiresAt ??
    params.activeOverrides[field]?.expiresAt ??
    params.pendingActions?.[field]?.expiresAt
  const trimmed = typeof candidate === 'string' ? candidate.trim() : ''
  return trimmed ? trimmed : undefined
}

export function buildManualOverrideActions(
  params: BuildManualOverrideActionsParams,
): BuildManualOverrideActionsResult {
  const actions: Record<string, ManualOverrideActionPayload> = {}
  const blockedFields: string[] = []
  const missingExpiresAtFields: string[] = []

  const removed = new Set(params.removedOverrideFields ?? [])

  // REMOVE takes precedence: a field staged for release is not also staged as add/update.
  for (const field of removed) {
    if (params.activeOverrides[field]) {
      actions[field] = { action: 'REMOVE_OVERRIDE' }
    }
  }

  for (const [field, manualValue] of Object.entries(params.dirtyFields)) {
    if (removed.has(field)) continue

    const source = firstResolvedSource(params.provenance[field])

    const pending = params.pendingActions?.[field]
    const hasExisting =
      Boolean(params.activeOverrides[field]) ||
      pending?.action === 'ADD_OVERRIDE' ||
      pending?.action === 'UPDATE_OVERRIDE'
    const mappedValueAtLock = normalizeMappedValueAtLock(resolveMappedValueAtLock(field, params))

    const releasePolicy = resolveFieldReleasePolicy(field, params)
    let expiresAt: string | undefined
    if (releasePolicy === 'UNTIL_DATE') {
      expiresAt = resolveFieldExpiresAt(field, params)
      if (!expiresAt) {
        missingExpiresAtFields.push(field)
        continue
      }
    }

    actions[field] = {
      action: hasExisting ? 'UPDATE_OVERRIDE' : 'ADD_OVERRIDE',
      manualValue,
      mappedValueAtLock,
      releasePolicy,
      ...(expiresAt ? { expiresAt } : {}),
      ...(source
        ? {
            sourceFeed: source.sourceFeed,
            sourceField: source.sourceField,
            sourcePath: source.sourcePath,
          }
        : {}),
    }
  }

  return { actions, blockedFields, missingExpiresAtFields }
}
